import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  TrashIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  TagIcon,
  EyeIcon,
  PencilIcon,
  CalendarIcon,
  InformationCircleIcon,
  CloudArrowUpIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { avatarApi, Avatar } from '../../services/avatarApi';
import Pagination, { paginate } from '@/components/Common/Pagination';
import ViewToggle, { ViewMode } from '@/components/Common/ViewToggle';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const ITEMS_PER_PAGE = 12;

const AvatarLibrary: React.FC = () => {
  const queryClient = useQueryClient();
  const { isFeatureEnabled } = useFeatureFlags();
  const avatarCreationEnabled = isFeatureEnabled('avatar-creation');

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [detailAvatar, setDetailAvatar] = useState<Avatar | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', tags: '' });
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [ownPage, setOwnPage] = useState(1);
  const [libraryPage, setLibraryPage] = useState(1);

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadForm, setUploadForm] = useState({ name: '', description: '', tags: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch avatars
  const { data, isLoading } = useQuery(
    ['avatars'],
    () => avatarApi.getAvatars(),
    { staleTime: 60_000 }
  );

  const ownAvatars: Avatar[] = data?.avatars?.own ?? [];
  const libraryAvatars: Avatar[] = data?.avatars?.library ?? [];

  // Upload mutation (only reachable while avatarCreationEnabled)
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => avatarApi.uploadAvatar(formData),
    onSuccess: () => {
      toast.success('Avatar uploaded');
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadPreview(null);
      setUploadForm({ name: '', description: '', tags: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries(['avatars']);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Upload failed');
    },
  });

  const handleFileSelect = (file: File | null) => {
    setUploadFile(file);
    if (!file) {
      setUploadPreview(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview((ev.target?.result as string) || null);
    reader.readAsDataURL(file);
  };

  const handleSubmitUpload = () => {
    if (!uploadFile) return toast.error('Please select an image file');
    if (!uploadForm.name.trim()) return toast.error('Name is required');
    const formData = new FormData();
    formData.append('image', uploadFile);
    formData.append('name', uploadForm.name.trim());
    if (uploadForm.description.trim()) formData.append('description', uploadForm.description.trim());
    if (uploadForm.tags.trim()) formData.append('tags', uploadForm.tags.trim());
    uploadMutation.mutate(formData);
  };

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string; tags?: string } }) =>
      avatarApi.updateAvatar(id, data),
    onSuccess: (res) => {
      toast.success('Avatar updated');
      setDetailAvatar(res.avatar);
      setEditMode(false);
      queryClient.invalidateQueries(['avatars']);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Update failed');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => avatarApi.deleteAvatar(id),
    onSuccess: () => {
      toast.success('Avatar deleted');
      setDeleteConfirmId(null);
      queryClient.invalidateQueries(['avatars']);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Delete failed');
    },
  });

  const openDetail = (avatar: Avatar) => {
    setDetailAvatar(avatar);
    setEditMode(false);
    setEditForm({
      name: avatar.name,
      description: avatar.description || '',
      tags: avatar.tags.join(', '),
    });
  };

  const handleEditSave = () => {
    if (!detailAvatar) return;
    if (!editForm.name.trim()) return toast.error('Name is required');
    editMutation.mutate({
      id: detailAvatar._id,
      data: {
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
        tags: editForm.tags.trim() || undefined,
      },
    });
  };

  // Filter helper
  const filterAvatars = (avatars: Avatar[]) =>
    searchQuery.trim()
      ? avatars.filter(
          a =>
            a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (a.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      : avatars;

  const filteredOwn = filterAvatars(ownAvatars);
  const filteredLibrary = filterAvatars(libraryAvatars);

  useEffect(() => {
    setOwnPage(1);
    setLibraryPage(1);
  }, [searchQuery]);

  const paginatedOwn = paginate(filteredOwn, ownPage, ITEMS_PER_PAGE);
  const paginatedLibrary = paginate(filteredLibrary, libraryPage, ITEMS_PER_PAGE);
  const ownTotalPages = Math.ceil(filteredOwn.length / ITEMS_PER_PAGE);
  const libraryTotalPages = Math.ceil(filteredLibrary.length / ITEMS_PER_PAGE);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Avatar Library</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Select an avatar in the Video Translation page to use it as the face for lip-sync.
          </p>
        </div>
        {avatarCreationEnabled && (
          <button
            onClick={() => setShowUploadForm(v => !v)}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            Upload Avatar
          </button>
        )}
      </div>

      {/* Upload form */}
      {avatarCreationEnabled && showUploadForm && (
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload New Avatar</h2>
            <button
              onClick={() => setShowUploadForm(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors flex flex-col items-center justify-center min-h-[160px]"
            >
              {uploadPreview ? (
                <img src={uploadPreview} alt="Preview" className="h-28 w-28 object-cover rounded-lg" />
              ) : (
                <>
                  <PhotoIcon className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Click to select an image</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP or GIF, up to 20MB</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={e => handleFileSelect(e.target.files?.[0] || null)}
              />
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={e => setUploadForm(p => ({ ...p, name: e.target.value }))}
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags <span className="text-xs text-gray-400">(comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={e => setUploadForm(p => ({ ...p, tags: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitUpload}
                  disabled={uploadMutation.isLoading}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploadMutation.isLoading ? (
                    <><div className="animate-spin h-4 w-4 rounded-full border-b-2 border-white" /> Uploading...</>
                  ) : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search + view toggle */}
      <div className="mb-6 flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search avatars by name, description or tag..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <ViewToggle value={viewMode} onChange={setViewMode} />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="space-y-10">
          {/* My Avatars — shown when the org already has some, or when the
              'avatar-creation' feature flag is on (so there's somewhere to
              see a freshly-uploaded avatar even before the org has any). */}
          {(ownAvatars.length > 0 || avatarCreationEnabled) && (
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              My Avatars
              <span className="ml-2 text-sm font-normal text-gray-500">({filteredOwn.length})</span>
            </h2>
            {filteredOwn.length === 0 ? (
              <div className="py-12 text-center text-gray-400 dark:text-gray-500 text-sm">
                {ownAvatars.length === 0 ? 'No custom avatars yet — upload one above.' : 'No matching avatars.'}
              </div>
            ) : viewMode === 'card' ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {paginatedOwn.map(avatar => (
                    <AvatarCard
                      key={avatar._id}
                      avatar={avatar}
                      onDelete={() => setDeleteConfirmId(avatar._id)}
                      onViewDetails={() => openDetail(avatar)}
                      showDelete
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={ownPage}
                  totalPages={ownTotalPages}
                  onPageChange={setOwnPage}
                  totalItems={filteredOwn.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </>
            ) : (
              <>
                <div className="space-y-2">
                  {paginatedOwn.map(avatar => (
                    <AvatarListRow
                      key={avatar._id}
                      avatar={avatar}
                      onDelete={() => setDeleteConfirmId(avatar._id)}
                      onViewDetails={() => openDetail(avatar)}
                      showDelete
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={ownPage}
                  totalPages={ownTotalPages}
                  onPageChange={setOwnPage}
                  totalItems={filteredOwn.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </>
            )}
          </section>
          )}

          {/* Library Avatars */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Avatar Library
              <span className="ml-2 text-sm font-normal text-gray-500">({filteredLibrary.length})</span>
            </h2>
            {filteredLibrary.length === 0 ? (
              <div className="py-12 text-center text-gray-400 dark:text-gray-500 text-sm">
                No library avatars available yet.
              </div>
            ) : viewMode === 'card' ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {paginatedLibrary.map(avatar => (
                    <AvatarCard key={avatar._id} avatar={avatar} onViewDetails={() => openDetail(avatar)} showDelete={false} />
                  ))}
                </div>
                <Pagination
                  currentPage={libraryPage}
                  totalPages={libraryTotalPages}
                  onPageChange={setLibraryPage}
                  totalItems={filteredLibrary.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </>
            ) : (
              <>
                <div className="space-y-2">
                  {paginatedLibrary.map(avatar => (
                    <AvatarListRow key={avatar._id} avatar={avatar} onViewDetails={() => openDetail(avatar)} showDelete={false} />
                  ))}
                </div>
                <Pagination
                  currentPage={libraryPage}
                  totalPages={libraryTotalPages}
                  onPageChange={setLibraryPage}
                  totalItems={filteredLibrary.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </>
            )}
          </section>
        </div>
      )}

      {/* Detail / Edit modal */}
      {detailAvatar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editMode ? 'Edit Avatar' : 'Avatar Details'}
              </h3>
              <div className="flex items-center gap-2">
                {!editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" /> Edit
                  </button>
                )}
                <button
                  onClick={() => { setDetailAvatar(null); setEditMode(false); }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Image */}
            <div className="px-6 pt-5">
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden flex items-center justify-center">
                {detailAvatar.imageUrl ? (
                  <img
                    src={avatarApi.getImageSrc(detailAvatar.imageUrl)}
                    alt={detailAvatar.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <UserCircleIcon className="h-24 w-24 text-gray-300 dark:text-gray-600" />
                )}
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              {editMode ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                      maxLength={100}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags <span className="text-xs text-gray-400">(comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.tags}
                      onChange={e => setEditForm(p => ({ ...p, tags: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setEditMode(false)}
                      className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleEditSave}
                      disabled={editMutation.isLoading}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {editMutation.isLoading ? (
                        <><div className="animate-spin h-4 w-4 rounded-full border-b-2 border-white" /> Saving...</>
                      ) : 'Save Changes'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{detailAvatar.name}</p>
                    {detailAvatar.description && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{detailAvatar.description}</p>
                    )}
                  </div>
                  {detailAvatar.tags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <TagIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      {detailAvatar.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <InformationCircleIcon className="h-3.5 w-3.5" /> File
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 truncate">{detailAvatar.originalName}</p>
                      <p className="text-xs text-gray-400">{(detailAvatar.size / 1024).toFixed(1)} KB · {detailAvatar.mimeType.split('/')[1].toUpperCase()}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <CalendarIcon className="h-3.5 w-3.5" /> Added
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{new Date(detailAvatar.createdAt).toLocaleDateString()}</p>
                      {detailAvatar.usageCount > 0 && (
                        <p className="text-xs text-green-600 dark:text-green-400">{detailAvatar.usageCount} use{detailAvatar.usageCount !== 1 ? 's' : ''}</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Avatar?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              This avatar will be removed from your library. Jobs that used it will not be affected.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface AvatarCardProps {
  avatar: Avatar;
  onDelete?: () => void;
  onViewDetails?: () => void;
  showDelete: boolean;
}

const AvatarCard: React.FC<AvatarCardProps> = ({ avatar, onDelete, onViewDetails, showDelete }) => {
  const imgSrc = avatarApi.getImageSrc(avatar.imageUrl);

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={avatar.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <UserCircleIcon className="h-16 w-16 text-gray-300 dark:text-gray-600" />
          </div>
        )}
      </div>

      {/* Library badge */}
      {avatar.isLibrary && (
        <div className="absolute top-2 left-2">
          <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded-full font-medium">
            Library
          </span>
        </div>
      )}

      {/* Hover actions */}
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="p-2 rounded-full bg-white/90 text-gray-700 hover:bg-white shadow-sm"
            title="View details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
        )}
        {showDelete && onDelete && (
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-2 rounded-full bg-white/90 text-red-600 hover:bg-white shadow-sm"
            title="Delete avatar"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-xs font-medium text-gray-900 dark:text-white truncate" title={avatar.name}>
          {avatar.name}
        </p>
        {avatar.tags.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            <TagIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-400 truncate">{avatar.tags.slice(0, 2).join(', ')}</p>
          </div>
        )}
        {avatar.usageCount > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <CheckCircleIcon className="h-3 w-3 text-green-400" />
            <p className="text-xs text-gray-400">Used {avatar.usageCount}×</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AvatarListRow: React.FC<AvatarCardProps> = ({ avatar, onDelete, onViewDetails, showDelete }) => {
  const imgSrc = avatarApi.getImageSrc(avatar.imageUrl);

  return (
    <div
      onClick={onViewDetails}
      className="flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow px-4 py-3 cursor-pointer"
    >
      <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
        {imgSrc ? (
          <img src={imgSrc} alt={avatar.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <UserCircleIcon className="h-7 w-7 text-gray-300 dark:text-gray-600" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{avatar.name}</p>
          {avatar.isLibrary && (
            <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded-full font-medium flex-shrink-0">
              Library
            </span>
          )}
        </div>
        {avatar.tags.length > 0 && (
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            <TagIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-400 truncate">{avatar.tags.join(', ')}</p>
          </div>
        )}
      </div>

      {avatar.usageCount > 0 && (
        <div className="hidden sm:flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
          <CheckCircleIcon className="h-3.5 w-3.5 text-green-400" />
          Used {avatar.usageCount}×
        </div>
      )}

      <div className="flex items-center gap-1 flex-shrink-0">
        {onViewDetails && (
          <button
            onClick={e => { e.stopPropagation(); onViewDetails(); }}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            title="View details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
        )}
        {showDelete && onDelete && (
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            title="Delete avatar"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AvatarLibrary;
